import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

/** Umlagerung. Das Backend entscheidet über Zulässigkeit und maximale Menge — hier wird nur
 *  der Serverzustand angezeigt und der Fehler des Servers gemeldet. */
export default function TransferDialog({ open, onClose, onDone, variant, lots, locations }) {
  const { t, fmtDate } = useI18n();
  const [lotKey, setLotKey] = useState("");
  const [target, setTarget] = useState("");
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const keyOf = (l) => `${l.warehouse_id}|${l.storage_location_id}|${l.lot_id || "-"}|${l.quality}`;
  useEffect(() => {
    if (!open) return;
    setLotKey(lots?.length === 1 ? keyOf(lots[0]) : "");
    setTarget(""); setQty(""); setReason("");
  }, [open, lots]);

  const src = (lots || []).find((l) => keyOf(l) === lotKey);
  const targets = (locations || []).filter(
    (l) => l.warehouse_id === src?.warehouse_id && l.active !== false
      && l.id !== src?.storage_location_id);

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/inventory/transfers", {
        variant_id: variant.variant_id, warehouse_id: src.warehouse_id,
        from_location_id: src.storage_location_id, to_location_id: target,
        lot_id: src.lot_id || null, quality: src.quality, quantity: Number(qty),
        reason, idempotency_key: `${variant.variant_id}-${lotKey}-${target}-${qty}-${Date.now()}`,
      });
      const to = (locations || []).find((l) => l.id === target);
      toast.success(t("tr.done", { qty: res.data.quantity,
        from: (locations || []).find((l) => l.id === src.storage_location_id)?.code || "",
        to: to?.code || "" }));
      onDone?.();
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="transfer-dialog">
        <DialogHeader><DialogTitle>{t("tr.title")} · {variant?.product_name || variant?.sku}</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">{t("tr.subtitle")}</p>
        <div className="space-y-3">
          <div>
            <Label>{t("tr.selectLot")}</Label>
            <Select value={lotKey} onValueChange={setLotKey}>
              <SelectTrigger className="mt-1.5" data-testid="transfer-lot"><SelectValue placeholder={t("tr.selectLot")} /></SelectTrigger>
              <SelectContent>
                {(lots || []).map((l) => (
                  <SelectItem key={keyOf(l)} value={keyOf(l)} data-testid={`transfer-lot-${l.lot_id || "nolot"}`}>
                    {(locations || []).find((x) => x.id === l.storage_location_id)?.code || "—"}
                    {" · "}{l.lot_number || t("ex.status.no_bbd")}
                    {l.best_before_date ? ` · ${fmtDate(l.best_before_date)}` : ""}
                    {` · ${t(`quality.${l.quality}`, l.quality)} · ${l.quantity}`}
                  </SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          {src && (
            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs" data-testid="transfer-source-info">
              <div>{t("tr.source")}: <span className="font-mono">{(locations || []).find((l) => l.id === src.storage_location_id)?.code}</span></div>
              <div>{t("tr.availableQty")}: <span className="font-mono font-semibold">{src.quantity}</span></div>
            </div>
          )}
          <div>
            <Label>{t("tr.target")}</Label>
            <Select value={target} onValueChange={setTarget} disabled={!src}>
              <SelectTrigger className="mt-1.5" data-testid="transfer-target"><SelectValue placeholder={t("ex.location")} /></SelectTrigger>
              <SelectContent>
                {targets.map((l) => (
                  <SelectItem key={l.id} value={l.id} data-testid={`transfer-target-${l.code}`}>{l.code}{l.name && l.name !== l.code ? ` · ${l.name}` : ""}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("common.quantity")}</Label>
            <Input type="number" min="1" className="mt-1.5" value={qty} data-testid="transfer-qty"
              onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <Label>{t("tr.reason")}</Label>
            <Input className="mt-1.5" value={reason} data-testid="transfer-reason"
              onChange={(e) => setReason(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="transfer-submit"
            disabled={busy || !src || !target || !(Number(qty) > 0)}>{t("tr.submit")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
